/**
 * Plantillas para el campo "Descripción del riesgo" del informe pericial.
 *
 * Tokens soportados (sustitución automática con datos del expediente):
 *   {municipio}      → extracted_data.municipio
 *   {provincia}      → extracted_data.provincia
 *   {antiguedad}     → años desde la fecha de construcción (si está disponible)
 *
 * Los placeholders *** se mantienen tal cual para que el perito los rellene a mano
 * (refs. catastrales, m², datos no disponibles en el parte).
 */

export const RIESGO_PLANTILLAS = [
  {
    id: "vivienda_unifamiliar",
    label: "Vivienda unifamiliar",
    description: "Adosada o aislada destinada a uso principal",
    text:
`El riesgo asegurado es una vivienda unifamiliar adosada destinada a uso principal ubicada en el municipio de {municipio} ({provincia}), encontrándose sin descampado a su alrededor. Presenta una tipología constructiva sólida y una calidad de los materiales de la construcción de tipo medio. El estado de conservación y mantenimiento de las instalaciones es normal. Su uso residencial es principal y no tenemos constancia de otras pólizas que aseguren el mismo riesgo. La superficie construida es de *** m² para la vivienda y *** m² dedicados a almacén. Su antigüedad es de *** años, según consta en el catastro.

Ref. Catastral: ***`,
  },
  {
    id: "piso",
    label: "Piso",
    description: "Vivienda dentro de bloque colectivo",
    text:
`El riesgo asegurado es una vivienda dentro de un bloque colectivo de viviendas destinada a uso principal ubicada en el municipio de {municipio} ({provincia}), encontrándose sin descampado a su alrededor. Presenta una tipología constructiva sólida y una calidad de los materiales de la construcción de tipo medio. El estado de conservación y mantenimiento de las instalaciones es normal. Su uso residencial es principal y no tenemos constancia de otras pólizas que aseguren el mismo riesgo. La superficie construida es de *** m² para la vivienda. Su antigüedad es de *** años, según consta en el catastro.

Ref. Catastral: ***`,
  },
  {
    id: "comercio",
    label: "Comercio",
    description: "Local comercial",
    text:
`El riesgo asegurado se trata de un local comercial destinado a *** [AÑADIR POR EL PERITO] en el municipio de {municipio} ({provincia}), encontrándose sin descampado a su alrededor. Presenta una tipología constructiva sólida y una calidad de los materiales de la construcción de tipo medio. El estado de conservación y mantenimiento de las instalaciones es normal. Su uso es comercial y no tenemos constancia de otras pólizas que aseguren el mismo riesgo. La superficie construida es de *** m² para el local comercial. Su antigüedad es de *** años, según consta en el catastro.

Ref. Catastral: ***`,
  },
  {
    id: "industria",
    label: "Industria",
    description: "Nave industrial",
    text:
`El riesgo asegurado se trata de una nave industrial destinada a *** [AÑADIR POR EL PERITO] en el municipio de {municipio} ({provincia}), encontrándose sin descampado a su alrededor. Presenta una tipología constructiva sólida y una calidad de los materiales de la construcción de tipo medio. El estado de conservación y mantenimiento de las instalaciones es normal. Su uso es industrial y no tenemos constancia de otras pólizas que aseguren el mismo riesgo. La superficie construida es de *** m², con *** m² para la industria y *** m² dedicados a oficinas. Su antigüedad es de *** años, según consta en el catastro.

Ref. Catastral: ***`,
  },
  {
    id: "comunidad",
    label: "Comunidad propietarios",
    description: "Bloque · comunidad de propietarios",
    text:
`El riesgo asegurado se trata de un bloque colectivo de viviendas, con garajes y locales destinadas a uso principal. Se trata de una edificación de sólida construcción en el municipio de {municipio} ({provincia}). El estado de conservación y mantenimiento de las instalaciones es normal. No tenemos constancia de otras pólizas que aseguren el mismo riesgo. La superficie de la edificación es de *** m² y su antigüedad es de *** años, según consta en el catastro.

Parcela catastral: ***`,
  },
];

/**
 * Rellena los tokens {municipio}, {provincia}, {antiguedad} de una plantilla con
 * los datos disponibles del expediente. Los que no estén disponibles se mantienen
 * como token literal en mayúsculas para que destaquen y el perito los complete.
 */
export function fillRiesgoTemplate(template, exp) {
  const ed = (exp && exp.extracted_data) || {};
  const mapping = {
    municipio: ed.municipio || "***",
    provincia: ed.provincia || "***",
    causa: ed.causa || "***",
    aseguradora: ed.aseguradora || "***",
  };
  return template.replace(/\{(\w+)\}/g, (match, key) => mapping[key] ?? match);
}

// =====================================================================
// Árbol de decisiones de "Aplicación de la póliza"
// =====================================================================
//
// Estructura:
//   Sí       → reparadores | indemnizacion | ambas
//   No       → (texto directo, sin segundo nivel)
//   Parcial  → reparadores | indemnizacion | ambas | pendiente_certificacion
//
// El token {causa} se sustituye con `extracted_data.causa` del expediente.

export const POLIZA_DECISIONES = [
  {
    id: "si",
    label: "Sí — Cobertura completa",
    short: "Sí",
    tone: "success",
    description: "El siniestro está plenamente cubierto",
    opciones: [
      {
        id: "si_reparadores",
        label: "Atención mediante reparadores",
        text:
`Por lo expuesto y conforme a lo establecido en las CCGG, CCPP y garantías contratadas en póliza, el siniestro tendría cobertura conforme a la garantía {causa} en cuanto a los daños por lo que emitimos las correspondientes órdenes de trabajo.`,
      },
      {
        id: "si_indemnizacion",
        label: "Valoración e indemnización",
        text:
`Por lo expuesto y conforme a lo establecido en las CCGG, CCPP y garantías contratadas en póliza el siniestro tendría cobertura conforme a la garantía {causa} por lo que realizamos la correspondiente propuesta de indemnización.`,
      },
      {
        id: "si_ambas",
        label: "Ambas — Reparación + indemnización",
        text:
`Por lo expuesto y conforme a lo establecido en las CCGG, CCPP y garantías contratadas en póliza el siniestro tendría cobertura conforme a la garantía {causa} por lo que realizamos la correspondiente propuesta de indemnización y emitimos las correspondientes órdenes de trabajo.`,
      },
    ],
  },
  {
    id: "no",
    label: "No — Sin cobertura",
    short: "No",
    tone: "danger",
    description: "El siniestro no está cubierto",
    text:
`Por lo expuesto y conforme a lo establecido en las CCGG, CCPP y garantías contratadas en póliza el siniestro no tendría cobertura. Valor aproximado de daños: ***`,
  },
  {
    id: "parcial",
    label: "Parcial — Cobertura limitada",
    short: "Parcial",
    tone: "warning",
    description: "Solo parte del siniestro está cubierta",
    opciones: [
      {
        id: "parcial_reparadores",
        label: "Atención mediante reparadores",
        text:
`Por lo expuesto y conforme a lo establecido en las CCGG, CCPP y garantías contratadas en póliza, el siniestro tendría cobertura únicamente en cuanto a los daños conforme a la garantía {causa} en cuanto a los daños por lo que emitimos las correspondientes órdenes de trabajo.`,
      },
      {
        id: "parcial_indemnizacion",
        label: "Valoración e indemnización",
        text:
`Por lo expuesto y conforme a lo establecido en las CCGG, CCPP y garantías contratadas en póliza el siniestro tendría cobertura únicamente en cuanto a los daños conforme a la garantía {causa} por lo que realizamos la correspondiente propuesta de indemnización.`,
      },
      {
        id: "parcial_ambas",
        label: "Ambas — Reparación + indemnización",
        text:
`Por lo expuesto y conforme a lo establecido en las CCGG, CCPP y garantías contratadas en póliza el siniestro tendría cobertura únicamente en cuanto a los daños conforme a la garantía {causa} por lo que realizamos la correspondiente propuesta de indemnización y emitimos las correspondientes órdenes de trabajo.`,
      },
      {
        id: "parcial_pendiente",
        label: "Pendiente de certificación de reparación de origen",
        text:
`Por lo expuesto y conforme a lo establecido en las CCGG, CCPP y garantías contratadas en póliza el siniestro tendría cobertura en cuanto a los daños por lo que anotamos en este apartado las correspondientes órdenes de trabajo que podrán ser ejecutadas una vez que se certifique la reparación del origen de los daños, no atendiendo nuevos siniestros con este mismo origen por la omisión de la reparación de la instalación causante.`,
      },
    ],
  },
];


